import { CLIENT_EVENT, EventLogger } from "@app/config/loggers/eventLogger";
import { useDeleteConfirmDialog } from "@base/model/hooks";
import { AppLink } from "@base/ui/AppLink";
import { Button, Chip, Dropdown } from "@buildblockinc/buildblock-design-system";
import { useDeleteDraftMutation } from "@features/draft/model/useDraftListQuery";
import dayjs from "dayjs";
import { EllipsisVertical, FilePen } from "lucide-react";
import { DocFileIcon } from "./DocFileIcon";

type DraftDocumentListItemProps = {
  projectId: number;
  draftId: string;
  title: string;
  updatedAt?: string;
};

export function DraftDocumentListItem({ projectId, draftId, title, updatedAt }: DraftDocumentListItemProps) {
  const deleteDraft = useDeleteDraftMutation();
  const { openDeleteDialog } = useDeleteConfirmDialog();

  const handleOpenDeleteDialog = () => {
    openDeleteDialog({
      description: "You will be unable to recover this draft.",
      onDelete: async () => {
        await deleteDraft.mutateAsync({ projectId, draftId });
        EventLogger.result(CLIENT_EVENT.DRAFT_DELETED, { project_id: String(projectId), draft_id: draftId });
      },
    });
  };

  return (
    <li className="flex gap-2 items-center md:gap-4 md:items-start md:px-3 md:py-3 md:hover:bg-gray-10">
      <AppLink
        to="/projects/$projectId/log/new"
        params={{ projectId: String(projectId) }}
        search={draftId ? { draftId } : {}}
        className="flex overflow-hidden flex-1 gap-2 items-center py-2 md:gap-4 md:py-0"
      >
        <span className="md:hidden">
          <DocFileIcon />
        </span>
        <span
          aria-hidden="true"
          className="hidden justify-center items-center w-14 h-14 rounded-8 shrink-0 bg-orange-10 text-orange-700 md:flex"
        >
          <FilePen width={22} height={22} />
        </span>
        <div className="flex overflow-hidden flex-col flex-1 gap-1 md:gap-1.5 md:justify-center md:self-stretch">
          <span className="flex md:hidden">
            <Chip size="xs" variant="fill" color="warn">
              Draft
            </Chip>
          </span>
          <div className="flex gap-2 items-center min-w-0">
            <p className="truncate text-body4 md:text-subtitle4 md:text-gray-1000">{title}</p>
            <span className="hidden shrink-0 md:inline-flex">
              <Chip size="xs" variant="fill" color="warn">
                Draft
              </Chip>
            </span>
          </div>
          {updatedAt && (
            <p className="hidden text-label3 text-gray-600 md:block">
              {`Last saved ${dayjs(updatedAt).format("ddd, MMM D, YYYY HH:mm")}`}
            </p>
          )}
        </div>
      </AppLink>
      <Dropdown>
        <Dropdown.Trigger
          className="p-1 text-gray-700 rounded-8 shrink-0 md:hover:bg-gray-100"
          aria-label="Draft actions"
        >
          <EllipsisVertical width={20} height={20} />
        </Dropdown.Trigger>
        <Dropdown.Content className="p-2">
          <Dropdown.Item value="delete">
            <Button
              type="button"
              className="px-2 py-3 text-red-500"
              variant="ghost"
              color="secondary"
              size="large"
              onClick={handleOpenDeleteDialog}
            >
              Delete Draft
            </Button>
          </Dropdown.Item>
        </Dropdown.Content>
      </Dropdown>
    </li>
  );
}
